import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { PatientFile } from '@/api/files/filesApi.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { formatFileSize } from '@/utils/fileSize';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { useApproveFileMutation, useRejectFileMutation } from '@/hooks/useFilesQueries';
import { ExerciseVideoLabel } from './ExerciseVideoLabel';
import { resolveFileIcon, type FilePreviewTarget } from './fileRowSupport';
import { RejectFileDialog } from './RejectFileDialog';

interface PendingFileRowProps {
  file: PatientFile;
  patientId: string;
  onPreview: (target: FilePreviewTarget) => void;
}

export function PendingFileRow({ file, patientId, onPreview }: PendingFileRowProps) {
  const { t } = useTranslation('files');
  const approveMutation = useApproveFileMutation();
  const isApproving = approveMutation.isPending;
  const rejectMutation = useRejectFileMutation();
  const [isRejecting, setIsRejecting] = useState(false);

  function preview() {
    if (file.downloadUrl === null) {
      return;
    }
    onPreview({
      fileId: file.id,
      url: file.downloadUrl,
      filename: file.filename,
      contentType: file.contentType,
    });
  }

  function approve() {
    approveMutation.mutate(
      { fileId: file.id, patientId },
      {
        onSuccess: function done() {
          showSuccessNotification(t('toasts.approved'));
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('toasts.approveError'));
        },
      }
    );
  }

  function reject(reviewNote: string | null) {
    rejectMutation.mutate(
      { fileId: file.id, patientId, reviewNote },
      {
        onSuccess: function done() {
          showSuccessNotification(t('toasts.rejected'));
          setIsRejecting(false);
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('toasts.rejectError'));
        },
      }
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 border border-zooborder rounded-xl bg-zoovpalelavender bg-opacity-30">
      {file.thumbnailUrl === null ? (
        <span className="w-9 h-9 rounded-lg bg-zoowhite text-zooprimary flex items-center justify-center shrink-0">
          <Icon name={resolveFileIcon(file.contentType)} />
        </span>
      ) : (
        <img
          src={file.thumbnailUrl}
          alt={file.filename}
          className="w-12 h-12 rounded-lg object-cover shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        {file.downloadUrl === null ? (
          <p className="text-sm font-semibold text-zooindigo truncate">{file.filename}</p>
        ) : (
          <button
            type="button"
            onClick={preview}
            className="text-sm font-semibold text-zooindigo truncate hover:text-zooprimary hover:underline text-left"
          >
            {file.filename}
          </button>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <Badge tone="muted" size="sm">
            {formatFileSize(file.sizeBytes)}
          </Badge>
          <ExerciseVideoLabel patientId={patientId} exerciseId={file.treatmentPlanExerciseId} />
          <span className="text-[11px] text-zoomuted">
            {formatIsoDateTimeToTimestamp(file.createdAt)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap justify-end w-full sm:w-auto sm:shrink-0">
        <Button
          variant="destructiveGhost"
          size="chip"
          className="border border-zooborder"
          disabled={isApproving}
          onClick={function openRejectDialog() {
            setIsRejecting(true);
          }}
        >
          {t('actions.reject')}
        </Button>
        <Button variant="primary" size="chip" isLoading={isApproving} onClick={approve}>
          {t('actions.approve')}
        </Button>
      </div>
      {isRejecting === true ? (
        <RejectFileDialog
          filename={file.filename}
          isSubmitting={rejectMutation.isPending}
          onConfirm={reject}
          onCancel={function closeRejectDialog() {
            setIsRejecting(false);
          }}
        />
      ) : null}
    </div>
  );
}
